import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { partnersByRegion } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const Partners = () => {
  const { tx, language } = useLanguage();
  const fr = language === 'fr';
  return (
    <div className="min-h-screen pt-[76px]" style={{ backgroundColor: '#000' }}>
      <SEO
        title="Partners & Dealers"
        description="Find authorized Total Composites partners and dealers across North America and worldwide. Become a partner and join our network of expedition vehicle specialists."
        path="/partners"
      />
      {/* Header */}
      <section className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>
            {fr ? 'Notre réseau' : 'Our Network'}
          </p>
          <h1 className="text-4xl lg:text-5xl font-black text-white mb-4 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fr ? 'Partenaires et constructeurs' : 'Partners & Builders'}
          </h1>
          <p className="text-gray-400 max-w-2xl leading-relaxed text-sm">
            {fr
              ? "TOTAL COMPOSITES travaille avec un réseau soigneusement sélectionné de constructeurs professionnels, de revendeurs et d’entreprises de fabrication à travers le monde. Ce sont les personnes et organisations à qui nous faisons confiance pour représenter nos produits, construire avec nos matériaux et offrir la qualité que nos clients attendent."
              : "TOTAL COMPOSITES works with a carefully selected network of professional builders, dealers, and fabrication companies worldwide. These are the people and organizations we trust to represent our products, build with our materials, and deliver the quality our customers expect."}
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-12 bg-gray-50 border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 gap-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#E8520A' }}>
                {fr ? 'Partenaires de projets' : 'Project Partners'}
              </p>
              <p className="text-gray-600 text-sm leading-relaxed">
                {fr
                  ? "Entreprises et fabricants avec lesquels nous collaborons sur des constructions personnalisées, des projets commerciaux et des applications spécialisées. Chaque partenaire apporte une expertise approfondie en aménagement de véhicules, en constructions d’expédition ou en construction composite."
                  : "Companies and fabricators we collaborate with on custom builds, commercial projects, and specialized applications. Each partner brings deep expertise in vehicle upfitting, expedition builds, or composite construction."}
              </p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#E8520A' }}>
                {fr ? 'Constructeurs sélectionnés' : 'Selected Builders'}
              </p>
              <p className="text-gray-600 text-sm leading-relaxed">
                {fr
                  ? "Professionnels vérifiés ayant complété notre programme de formation et livrant systématiquement des constructions finies conformes à nos standards. Contactez-les directement pour les prix, la disponibilité et les délais de projet."
                  : "Vetted professionals who have completed our training program and consistently deliver finished builds that meet our standards. Reach out to them directly for pricing, availability, and project timelines."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partners by Region */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {partnersByRegion.map((region) => (
            <div key={region.region} className="mb-16 last:mb-0">
              <div className="flex items-center gap-4 mb-8">
                <h2 className="text-2xl font-black text-gray-900" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {tx(region.region)}
                </h2>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {region.partners.map((partner) => (
                  <a
                    key={partner.name}
                    href={partner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group border border-gray-200 rounded-xl p-6 hover:border-orange-300 hover:shadow-lg transition-all duration-300 flex flex-col items-center text-center"
                  >
                    <div className="h-16 flex items-center justify-center mb-4">
                      <img
                        src={partner.logo}
                        alt={partner.name}
                        className="max-h-7 max-w-full object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
                      />
                    </div>
                    <h3 className="text-xs font-bold uppercase tracking-wide text-gray-800 mb-1 group-hover:text-orange-600 transition-colors">
                      {partner.name}
                    </h3>
                    <p className="text-xs text-gray-400 mb-3">{partner.location}</p>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold mt-auto" style={{ color: '#E8520A' }}>
                      {fr ? 'VISITER LE SITE' : 'VISIT WEBSITE'} <ExternalLink size={11} />
                    </span>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Partners Map */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>
            {fr ? 'Emplacements des partenaires' : 'Partner Locations'}
          </p>
          <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
            <iframe
              src="https://www.google.com/maps/d/u/0/embed?mid=19WyoeyDX_K4MXv0Xix-cwoteMyEeAwg&ehbc=2E312F"
              width="100%"
              height="480"
              frameBorder="0"
              title={fr ? 'Emplacements des partenaires TOTAL COMPOSITES' : 'TOTAL COMPOSITES Partner Locations'}
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* Become a Partner */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-black text-gray-900 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fr ? 'Rejoignez notre réseau' : 'Join Our Network'}
          </h2>
          <p className="text-gray-500 mb-10 text-sm leading-relaxed">
            {fr
              ? "Nous recherchons toujours des constructeurs, fabricants et revendeurs sérieux partageant notre engagement envers la qualité. Si vous travaillez dans le domaine des VR, du overland ou des véhicules commerciaux et souhaitez construire avec les meilleurs systèmes composites du marché, contactez-nous."
              : "We're always looking for serious builders, fabricators, and dealers who share our commitment to quality. If you work in the RV, overland, or commercial vehicle space and want to build with the best composite systems on the market, get in touch."}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-7 py-3.5 font-bold text-white rounded-full text-sm transition-all duration-200 hover:opacity-90 hover:scale-105"
            style={{ backgroundColor: '#E8520A' }}
          >
            {fr ? 'POSTULER MAINTENANT' : 'APPLY NOW'} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Partners;
